import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Terminal, Plus, Trash2, Pencil, Shuffle, LayoutTemplate } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { Toggle, PageHead } from "@/components/kit";

const BLANK = { name: "", response: "", enabled: true, cooldown: 0, permission: "everyone",
  responses: [], embed: false, embed_title: "", embed_color: "#0EA5E9" };

const VARS = [
  { tag: "{user}", desc: "mentions the user" },
  { tag: "{username}", desc: "their display name" },
  { tag: "{server}", desc: "server name" },
  { tag: "{count}", desc: "member count" },
  { tag: "{channel}", desc: "current channel" },
];

export default function CustomCommands() {
  const { guildId } = useGuild();
  const [cmds, setCmds] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [randomText, setRandomText] = useState("");

  const load = () => api.get(`/guilds/${guildId}/commands`).then((r) => setCmds(r.data));
  useEffect(() => { load(); }, [guildId]);

  const reset = () => { setForm(BLANK); setRandomText(""); setEditing(null); };

  const submit = async () => {
    const responses = randomText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!form.name) return toast.error("Command name required");
    if (!form.response && responses.length === 0) return toast.error("Add a response or at least one random reply");
    const payload = { ...form, responses };
    try {
      if (editing) await api.put(`/guilds/${guildId}/commands/${editing}`, payload);
      else await api.post(`/guilds/${guildId}/commands`, payload);
      toast.success(editing ? "Command updated" : "Command created");
      reset(); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this command?")) return; await api.delete(`/guilds/${guildId}/commands/${id}`); toast.success("Deleted"); load(); };
  const edit = (c) => {
    setEditing(c.id);
    setForm({ name: c.name, response: c.response || "", enabled: c.enabled, cooldown: c.cooldown || 0,
      permission: c.permission || "everyone", responses: c.responses || [], embed: !!c.embed,
      embed_title: c.embed_title || "", embed_color: c.embed_color || "#0EA5E9" });
    setRandomText((c.responses || []).join("\n"));
  };

  const inputCls = "mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm";
  const labelCls = "text-xs uppercase tracking-wider text-zinc-500";

  return (
    <div>
      <PageHead title="Custom Commands" desc="Create text commands your members can trigger." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4 h-fit">
          <h3 className="font-head font-semibold flex items-center gap-2"><Terminal className="w-4 h-4 text-volt" /> {editing ? "Edit command" : "New command"}</h3>
          <div><label className={labelCls}>Trigger</label>
            <div className="mt-1 flex items-center bg-black border border-zinc-800 rounded-md focus-within:border-volt">
              <span className="pl-3 text-zinc-500 font-mono">!</span>
              <input data-testid="cmd-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="hello" className="flex-1 bg-transparent px-2 py-2 outline-none font-mono" />
            </div></div>

          <div><label className={labelCls}>Response</label>
            <textarea data-testid="cmd-response" value={form.response} onChange={(e) => setForm({ ...form, response: e.target.value })} rows={3} placeholder="Hi {user}, welcome to {server}! We have {count} members." className={inputCls} /></div>

          {/* Variable helper */}
          <div className="flex flex-wrap gap-1.5" data-testid="cmd-vars">
            {VARS.map((v) => (
              <button key={v.tag} type="button" title={v.desc}
                onClick={() => setForm((f) => ({ ...f, response: (f.response || "") + v.tag }))}
                className="text-[11px] font-mono bg-black/50 border border-zinc-800 rounded px-1.5 py-0.5 text-volt hover:border-volt/50">
                {v.tag}
              </button>
            ))}
          </div>

          {/* Random responses */}
          <div><label className={`${labelCls} flex items-center gap-1.5`}><Shuffle className="w-3.5 h-3.5 text-volt" /> Random replies (one per line)</label>
            <textarea data-testid="cmd-random" value={randomText} onChange={(e) => setRandomText(e.target.value)} rows={3} placeholder={"First option\nSecond option\nThird option"} className={inputCls} />
            <p className="text-[11px] text-zinc-600 mt-1">If filled, the bot picks one of these at random (variables still work). Leave empty to always use the response above.</p></div>

          {/* Embed */}
          <div className="flex items-center justify-between"><span className="text-sm text-zinc-400 flex items-center gap-1.5"><LayoutTemplate className="w-4 h-4 text-volt" /> Send as embed</span><Toggle testid="cmd-embed" checked={form.embed} onChange={(v) => setForm({ ...form, embed: v })} /></div>
          {form.embed && (
            <div className="flex gap-3">
              <div className="flex-1"><label className={labelCls}>Embed title</label>
                <input data-testid="cmd-embed-title" value={form.embed_title} onChange={(e) => setForm({ ...form, embed_title: e.target.value })} placeholder="optional" className={inputCls} /></div>
              <div><label className={labelCls}>Color</label>
                <input data-testid="cmd-embed-color" type="color" value={form.embed_color} onChange={(e) => setForm({ ...form, embed_color: e.target.value })} className="mt-1 w-12 h-10 bg-black border border-zinc-800 rounded-md cursor-pointer" /></div>
            </div>
          )}

          <div className="flex items-center justify-between"><span className="text-sm text-zinc-400">Enabled</span><Toggle testid="cmd-enabled" checked={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} /></div>
          <div className="flex gap-3">
            <div className="flex-1"><label className={labelCls}>Cooldown (s) <span className="text-volt">PRO</span></label>
              <input data-testid="cmd-cooldown" type="number" min="0" value={form.cooldown} onChange={(e) => setForm({ ...form, cooldown: +e.target.value })} className={inputCls} /></div>
            <div className="flex-1"><label className={labelCls}>Permission <span className="text-volt">PRO</span></label>
              <select data-testid="cmd-permission" value={form.permission} onChange={(e) => setForm({ ...form, permission: e.target.value })} className={inputCls}>
                <option value="everyone">Everyone</option><option value="moderator">Moderators</option><option value="admin">Admins</option>
              </select></div>
          </div>
          <button data-testid="cmd-submit" onClick={submit} className="w-full bg-volt text-volt-ink font-semibold rounded-md py-2 hover:bg-volt-hover flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> {editing ? "Update" : "Create"}</button>
          {editing && <button onClick={reset} className="w-full text-sm text-zinc-500 hover:text-white">Cancel edit</button>}
        </div>

        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-head font-semibold mb-4">Commands ({cmds.length})</h3>
          <div className="space-y-2">
            {cmds.map((c) => (
              <div key={c.id} data-testid="cmd-row" className="flex items-center gap-3 bg-black/30 border border-zinc-800 rounded-lg p-3">
                <code className="font-mono text-volt">!{c.name}</code>
                <span className="text-sm text-zinc-400 truncate flex-1">{(c.responses && c.responses.length > 0) ? c.responses[0] : c.response}</span>
                {c.responses && c.responses.length > 0 && <span data-testid="cmd-random-badge" title={`${c.responses.length} random replies`} className="text-[10px] uppercase bg-[#f59e0b]/15 text-[#f59e0b] border border-[#f59e0b]/30 rounded px-1.5 py-0.5 flex items-center gap-1"><Shuffle className="w-3 h-3" /> {c.responses.length}</span>}
                {c.embed && <span data-testid="cmd-embed-badge" title="Sends as an embed" className="text-[10px] uppercase bg-volt/15 text-volt border border-volt/30 rounded px-1.5 py-0.5">embed</span>}
                <span className="text-xs text-zinc-600">{c.uses} uses</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${c.enabled ? "bg-volt/15 text-volt" : "bg-zinc-800 text-zinc-500"}`}>{c.enabled ? "on" : "off"}</span>
                <button data-testid="cmd-edit" onClick={() => edit(c)} className="text-zinc-500 hover:text-white"><Pencil className="w-4 h-4" /></button>
                <button data-testid="cmd-delete" onClick={() => del(c.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {cmds.length === 0 && <p className="text-zinc-600 text-sm">No commands yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
